import { Box, Divider, Link, Stack, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getGitHubRepositories,
  getGitHubRepositoryBranches,
  integrateProjectWithGitHubRepo,
  qk,
} from 'api'
import { env } from 'app/env'
import { IconGitHub } from 'assets/icons'
import { DialogContent, ToastContent } from 'elements/feedback'
import { Autocomplete, renderTextField } from 'elements/inputs'
import { LinkButton } from 'elements/navigation'
import { getInputError, useValidations } from 'lib/form-utils'
import { useBoolean, useSearch } from 'lib/hooks'
import { useInfinitePaginatedQuery } from 'lib/react-query-utils'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { IntegrationModalHeader } from '../integration-modal-header'

const formId = 'CREATE_GITHUB_INTEGRATION_FORM'

type FormValues = {
  baseBranch: string
  pathTemplate: string
  repo: string
  username: string
}

type CreateGitHubIntegrationFormProps = {
  projectId: string
  closeDialog: () => void
}

export const CreateGitHubIntegrationForm = ({
  projectId,
  closeDialog,
}: CreateGitHubIntegrationFormProps) => {
  const { t } = useTranslation()

  const isReposEnabled = useBoolean()
  const isBranchesEnabled = useBoolean()

  const { search: searchBranches, onChange: onBranchSearch } = useSearch()
  const { search: searchRepos, onChange: onReposSearch } = useSearch()

  const rules = useValidations()

  const queryClient = useQueryClient()

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      baseBranch: '',
      pathTemplate: '',
      repo: '',
      username: '',
    },
  })

  const username = watch('username')
  const repo = watch('repo')
  const branch = watch('baseBranch')

  useEffect(() => {
    setValue('repo', '')
  }, [setValue, username])

  useEffect(() => {
    setValue('baseBranch', '')
  }, [setValue, repo])

  const $integrateProjectWithGitHubRepo = useMutation(
    integrateProjectWithGitHubRepo,
  )

  const $githubRepositories = useInfinitePaginatedQuery(
    qk.github.repositories,
    getGitHubRepositories,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      username,
      search: searchRepos,
    },
    {
      enabled: username !== '' && isReposEnabled.isTrue,
      onError: error => {
        toast.error(
          <ToastContent title={(error as { message: string }).message} />,
        )
      },
    },
  )

  const $githubBranches = useInfinitePaginatedQuery(
    qk.github.repositories,
    getGitHubRepositoryBranches,
    {
      pagination: {
        page: 0,
        pageSize: 50,
      },
      username,
      repoName: repo,
      search: searchBranches,
    },
    {
      enabled: username !== '' && repo !== '' && isBranchesEnabled.isTrue,
      onError: error => {
        toast.error(
          <ToastContent title={(error as { message: string }).message} />,
        )
      },
    },
  )

  const [usernameValue, repoValue] = watch(['username', 'repo'])

  return $integrateProjectWithGitHubRepo.status === 'success' ? (
    <DialogContent onDeny={closeDialog} hideCancelButton>
      <Stack alignItems="center" spacing={2}>
        <IconGitHub sx={{ fontSize: '60px', color: 'success.main' }} />
        <Typography variant="body3" fontWeight={600}>
          {t(
            'projects.settings.integrations.github_integrated_with_your_project',
          )}
        </Typography>

        <Typography variant="caption2">
          <Trans
            i18nKey="projects.settings.integrations.you_have_github_integration_with_repo"
            values={{
              username: usernameValue,
              repo: repoValue,
            }}
            components={{
              linkElement: (
                <Typography
                  component={Link}
                  variant="inherit"
                  color="inherit"
                  href={`https://github.com/${usernameValue}/${repoValue}`}
                  target="_blank"
                  underline="hover"
                />
              ),
            }}
          />
        </Typography>
      </Stack>
    </DialogContent>
  ) : (
    <DialogContent
      title={t('projects.settings.integrations.create_github_integration')}
      isActionLoading={$integrateProjectWithGitHubRepo.isLoading}
      onDeny={closeDialog}
      externalFormId={formId}
      confirmText={t('projects.settings.integrations.make_connection')}
    >
      <Divider sx={{ my: 2 }} />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <IntegrationModalHeader
          title={t('projects.settings.integrations.github_integration_warning')}
        />

        <LinkButton to={env.REACT_APP_LIBRARIA_GITHUB_REPO} target="_blank">
          {t('projects.settings.integrations.go_to_github')}
        </LinkButton>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Stack
        id={formId}
        component="form"
        onSubmit={handleSubmit(values =>
          $integrateProjectWithGitHubRepo.mutate(
            {
              projectId,
              ...values,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(
                  qk.projects.reposList.toKeyWithArgs({ projectId }),
                )
              },
            },
          ),
        )}
        spacing={2}
        mt={2}
      >
        <Controller
          name="username"
          control={control}
          rules={rules.required}
          render={renderTextField({
            label: t('projects.settings.integrations.github_username'),
          })}
        />

        <Controller
          name="repo"
          control={control}
          rules={rules.required}
          render={({ field, fieldState }) => (
            <Autocomplete
              label={t('projects.settings.integrations.repository_name.github')}
              disableInternalFilter
              disableAlphabeticalOrder
              options={
                $githubRepositories.data?.pages.flatMap(page =>
                  page.rows.map(branch => ({
                    value: branch,
                    label: branch,
                  })),
                ) ?? []
              }
              onOpen={isReposEnabled.setTrue}
              onClose={isReposEnabled.setFalse}
              hasMoreOptions={$githubRepositories.hasNextPage}
              onLoadMore={$githubRepositories.fetchNextPage}
              isLoadingMore={
                $githubRepositories.isFetchingNextPage ||
                $githubRepositories.isLoading
              }
              disabled={username === ''}
              {...getInputError(fieldState.error)}
              {...field}
              onInputChange={onReposSearch}
            />
          )}
        />

        <Controller
          name="baseBranch"
          control={control}
          rules={rules.required}
          render={({ field, fieldState }) => (
            <Autocomplete
              label={t('projects.settings.integrations.base_branch')}
              disableInternalFilter
              disableAlphabeticalOrder
              options={
                $githubBranches.data?.pages.flatMap(page =>
                  page.rows.map(branch => ({
                    value: branch,
                    label: branch,
                  })),
                ) ?? []
              }
              onOpen={isBranchesEnabled.setTrue}
              onClose={isBranchesEnabled.setFalse}
              hasMoreOptions={$githubBranches.hasNextPage}
              onLoadMore={$githubBranches.fetchNextPage}
              isLoadingMore={
                $githubBranches.isFetchingNextPage || $githubBranches.isLoading
              }
              disabled={username === '' || repo === ''}
              {...getInputError(fieldState.error)}
              {...field}
              onInputChange={onBranchSearch}
            />
          )}
        />

        <Controller
          name="pathTemplate"
          control={control}
          rules={{ ...rules.pathTemplate, ...rules.required }}
          render={renderTextField({
            disabled: username === '' || repo === '' || branch === '',
            label: t('projects.settings.integrations.path_template'),
          })}
        />

        <Divider color="divider" sx={{ mt: 5, borderBottomWidth: 1 }} />
      </Stack>
    </DialogContent>
  )
}
